import { useRef, useState } from "react";
import WatchImage from "@/components/site/WatchImage";

/**
 * Zoomable watch image.
 * Default state: shows the image, cursor is `zoom-in`, faint "+ Zoom" hint top-right.
 * Click anywhere on the image → enters zoom mode (2.2x scale, transform-origin
 * follows pointer / touch position so the user pans by moving the cursor).
 * Click again → exits.
 *
 * Designed to give "close enough to read the bezel and dial" detail
 * without pixelating the artwork.
 */
export default function ZoomableImage({
  src,
  alt,
  fallbackTitle,
  fallbackSub,
  testId,
  zoom = 2.2,
}) {
  const wrapperRef = useRef(null);
  const [zoomed, setZoomed] = useState(false);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });

  const updateOrigin = (clientX, clientY) => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = Math.min(100, Math.max(0, ((clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((clientY - rect.top) / rect.height) * 100));
    setOrigin({ x, y });
  };

  const onMove = (e) => {
    if (!zoomed) return;
    updateOrigin(e.clientX, e.clientY);
  };

  const onTouchMove = (e) => {
    if (!zoomed) return;
    const t = e.touches[0];
    if (!t) return;
    updateOrigin(t.clientX, t.clientY);
  };

  const onClick = (e) => {
    if (!zoomed) {
      updateOrigin(e.clientX, e.clientY);
    }
    setZoomed((z) => !z);
  };

  return (
    <div
      ref={wrapperRef}
      role="button"
      tabIndex={0}
      aria-pressed={zoomed}
      aria-label={zoomed ? "Exit zoom" : "Zoom in"}
      data-testid={testId || "zoomable-image"}
      onClick={onClick}
      onMouseMove={onMove}
      onTouchMove={onTouchMove}
      onTouchStart={(e) => {
        const t = e.touches[0];
        if (t) updateOrigin(t.clientX, t.clientY);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          setZoomed((z) => !z);
        }
      }}
      style={{ cursor: zoomed ? "zoom-out" : "zoom-in" }}
      className="relative w-full h-full overflow-hidden select-none"
    >
      <div
        className="w-full h-full transition-transform duration-300 ease-out will-change-transform"
        style={{
          transform: zoomed ? `scale(${zoom})` : "scale(1)",
          transformOrigin: `${origin.x}% ${origin.y}%`,
        }}
      >
        <WatchImage
          src={src}
          alt={alt}
          fallbackTitle={fallbackTitle}
          fallbackSub={fallbackSub}
          loading="eager"
        />
      </div>

      {/* Quiet zoom indicator */}
      <div className="pointer-events-none absolute top-3 right-3 flex items-center gap-2 text-[9px] uppercase tracking-[0.42em] text-[#C5A059]">
        <span className="block w-4 h-px bg-[#C5A059]" />
        {zoomed ? "Zoomed" : "Zoom"}
      </div>
    </div>
  );
}
